import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useParams, withRouter } from 'react-router-dom';
import isNil from 'lodash/isNil';

import { useUserSettings } from '@Hooks/userHooks';

import RsModal from '@Pages/RetroSynthesis/components/RsModal';
import ExportRouteDialogContainer from '@Pages/RetroSynthesis/ExportRoute/ExportRouteDialogContainer';
import { structureOptionCheckedAction } from '@Pages/RetroSynthesis/StructureOption/actions';
import { getSynthesisMenuOptions } from '@Pages/RetroSynthesis/StructureOption/selectors';
import { StructureOptionType } from '@Pages/RetroSynthesis/StructureOption/constants';
import { showAddStepsDialog } from '@Pages/RetroSynthesis/EditPlan/AddSteps/actions';

import {
  onHamburgerMenuOpened,
  onShoppingCartOpened,
  onStepAvailableWithDelay,
  onPageLoad,
  onStepSelected,
  onZoom,
  onResize as onResizeAA,
  projectRouteViewTypes,
} from '@Pages/RetroSynthesis/ProjectRoute/analytics';
import {
  onAbstractOpened,
  onAuthorNameClicked,
  onCitedNTimesClicked,
  onExperimentalProcedureOpened,
  onFullTextLinkClicked,
  onSelectedClicked,
  onViewSelectedClicked,
} from '@Pages/RetroSynthesis/ProjectRoute/ProjectRouteConditions/analytics';
import { citationLiteratureFormatter } from '@Services/FormatUtils';

import { showExportDialog } from '@Pages/RetroSynthesis/ExportRoute/actions';
import { exportDialogIsShown } from '@Pages/RetroSynthesis/ExportRoute/selectors';
import { linkOptionsOpenAction } from '@Pages/RetroSynthesis/LinkOption/actions';
import AddStepsDialogContainer from '@Pages/RetroSynthesis/EditPlan/AddSteps/AddStepsDialogContainer';
import { addStepsDialogIsShown } from '@Pages/RetroSynthesis/EditPlan/AddSteps/selectors';
import { clearState, forceReload } from '@Pages/RetroSynthesis/components/RsSlidablePaginated/actions';
import { onAddStepDialogOpen } from '@Pages/RetroSynthesis/EditPlan/AddSteps/analytics';
import { onOptionClick } from '@Pages/RetroSynthesis/LinkOption/analytics';
import { getProjectsInfoMap } from '@Pages/RetroSynthesis/RsBreadcrumbs/selector';
import { RsRoute } from '@Pages/RetroSynthesis/routes';
import ProjectRouteConditions from './ProjectRouteConditions';
import ProjectRouteTreeView from './ProjectRouteTreeView';
import ProjectRouteToolbar from './ProjectRouteToolbar';
import {
  fetchConditionsStepData as fetchConditionsStepDataAction,
  fetchData as fetchDataAction,
  setActiveStep as setActiveStepAction,
  setConditionsIsShown as setConditionsIsShownAction,
  setConditionsPlacement as setConditionsPlacementAction,
  onConditionsUnmount as onConditionsUnmountAction,
  copyRoute,
  setIsTransferFromReaction,
  saveStepSelections as saveStepSelectionsAction,
  fetchPredictedLastStepData,
  clearDeletedRoute,
} from './actions';
import {
  activeStep as activeStepSelector,
  conditionsIsShown as conditionsIsShownSelector,
  conditionsPlacement as conditionsPlacementSelector,
  conditionsSteps as conditionsStepsSelector,
  deletedRoute,
  hasPredictedStep,
  isTransferFromReaction,
  routeType as routeTypeSelector,
  treeData as treeDataSelector,
} from './selectors';
import { DEFAULT_CONDITIONS_IS_SHOWN, DEFAULT_CONDITIONS_PLACEMENT } from './constants';
import { shouldFetchData } from './hooks';
/* eslint-disable react/prop-types */
/* eslint-disable no-shadow */
/* eslint-disable react-hooks/exhaustive-deps */
function ProjectRoute({
  fetchData,
  routeType,
  treeData,
  conditionsSteps,
  activeStep,
  setActiveStep,
  fetchConditionsStepData,
  conditionsPlacement,
  setConditionsPlacement,
  conditionsIsShown,
  setConditionsIsShown,
  onConditionsUnmount,
  // Sp menu options for structure buttons
  structureOptionChecked,
  showExportDialog,
  exportDialogIsShown,
  showAddStepsDialog,
  addStepsDialogIsShown,
  openLinkOptions,
  clearStateThumbnails,
  copyRoute,
  isTransferFromReaction,
  setIsTransferFromReaction,
  saveStepSelections,
  projectsInfoMap,
  history,
  deletedRoute,
  forceReloadThumbnails,
  loadLastExamples,
  clearDeletedRoute,
}) {
  const { projectId, routeId } = useParams();
  const [resized, setResized] = useState(undefined);
  const projectInfo = projectsInfoMap && projectsInfoMap[projectId];

  const onActiveStepChangeHandler = (newActiveStep) => {
    onStepSelected({ projectId, routeId, stepNumber: newActiveStep, routeType, projectInfo });
  };

  const onPageLoadHandler = (viewType, activeStep) => {
    onPageLoad({ viewType, projectId, routeId, stepNumber: activeStep, isTransferFromReaction, routeType, projectInfo });
    setIsTransferFromReaction(false); // reset transfer flag after tracking AA
  };

  const onStepAutoSetWithDelayHandler = (activeStep) => {
    onStepAvailableWithDelay({ projectId, routeId, stepNumber: activeStep, routeType, projectInfo });
  };

  const getCitationDataName = (citation) => {
    if (citation.type && (citation.title || citation.citationPatents || citation.citationJournals)) {
      const body = citation.title || citationLiteratureFormatter(citation.citationPatents || citation.citationJournals, true);
      return `${citation.type}:${body}`;
    }
    return '';
  };

  const onResizeAAHandler = () => {
    onResizeAA({ projectId, routeId, stepNumber: activeStep, routeType, projectInfo });
  };

  const saveStepSelectionsHandler = (data) => {
    saveStepSelections({ ...data, projectId, routeId });
    if (data && data.trackingData) {
      if (data.trackingData.viewSelected) {
        // track view selected
        onViewSelectedClicked({
          projectId,
          routeId,
          routeType,
          projectInfo,
          selected: data.selected,
          stepNumber: activeStep,
        });
      }

      if (data.trackingData.selectedResults) {
        // track results checkboxes
        onSelectedClicked({
          projectId,
          routeId,
          routeType,
          projectInfo,
          selectedResults: data.trackingData.selectedResults,
          stepNumber: activeStep,
        });
      }
    }
  };

  const getBaseConditionsAAPayload = (citation) => ({
    projectId,
    routeId,
    stepNumber: activeStep,
    dataId: citation.id,
    recordDate: citation.entryDate,
    currentDate: Date.now(),
    routeType,
    projectInfo,
  });

  const onConditionsFullTextLinkClickHandler = (citation) => {
    onFullTextLinkClicked({
      ...getBaseConditionsAAPayload(citation),
      dataName: getCitationDataName(citation),
      dataCitationType: citation.type,
      linkLocation: citation.fullTextUrl,
    });
  };

  const onConditionsIssueCitedCounterClickHandler = (citation) => {
    onCitedNTimesClicked({
      ...getBaseConditionsAAPayload(citation),
      dataName: getCitationDataName(citation),
      dataCitationType: citation.type,
      linkLocation: citation.fullTextUrl,
    });
  };

  const onConditionsExperimentalProcedureOpenHandler = (citation) => {
    onExperimentalProcedureOpened(getBaseConditionsAAPayload(citation));
  };

  const onConditionsAbstractOpenHandler = (citation) => {
    onAbstractOpened(getBaseConditionsAAPayload(citation));
  };

  const onConditionsAuthorClickHandler = (citation) => {
    onAuthorNameClicked({
      ...getBaseConditionsAAPayload(citation),
      dataName: getCitationDataName(citation),
      dataCitationType: citation.type,
      linkLocation: citation.fullTextUrl,
    });
  };

  const onAddNodeClickHandler = (data) => {
    showAddStepsDialog(data);
    onAddStepDialogOpen({ projectId, routeId, stepNumber: data.parentStepNumber, routeType, projectInfo });
  };

  // Fetch data
  shouldFetchData(
    () => {
      fetchData({ projectId, routeId, history });
    },
    { projectId, routeId }
  );

  const fetchStepData = (type, stepData) => fetchConditionsStepData(type, { ...stepData, projectId, routeId });

  // reload thumbnails if already loaded
  useEffect(() => {
    clearStateThumbnails();
  }, [projectId]);

  useEffect(() => {
    if (deletedRoute) {
      // reloaded route
      if (deletedRoute.toString() !== routeId) {
        // new
        history.push(RsRoute.getProjectRouteTreeViewLink(projectId, deletedRoute));
      } else {
        // updated
        fetchData({ projectId, routeId, cache: false });
      }
      forceReloadThumbnails(deletedRoute);
      clearDeletedRoute();
    }
  }, [deletedRoute]);

  // Initialize conditions placement from user settings just once...
  useUserSettings((userSettings) => {
    const { synthesisPlannerConditionsPosition, synthesisPlannerConditionsAlwaysShown } = userSettings;

    if (isNil(conditionsPlacement)) {
      setConditionsPlacement(synthesisPlannerConditionsPosition || DEFAULT_CONDITIONS_PLACEMENT);
    }

    if (isNil(conditionsIsShown)) {
      setConditionsIsShown(synthesisPlannerConditionsAlwaysShown || DEFAULT_CONDITIONS_IS_SHOWN);
    }
  });

  return (
    <div className="retro-synthesis-projects rx-flex-column-container u-els-height-1o1" id="main-content">
      <ProjectRouteToolbar
        conditionsIsShown={conditionsIsShown}
        setConditionsIsShown={setConditionsIsShown}
        onExport={() => showExportDialog()}
        routeType={routeType}
      />
      <RsModal showBackdrop showModal={exportDialogIsShown}>
        <ExportRouteDialogContainer />
      </RsModal>
      <RsModal showBackdrop showModal={addStepsDialogIsShown}>
        <AddStepsDialogContainer />
      </RsModal>
      <div className="rx-flex-row-container rx-flex-1 h0">
        <div className="rx-flex-column-container rx-flex-1">
          <ProjectRouteTreeView
            projectId={projectId}
            routeId={routeId}
            routeType={routeType}
            treeData={treeData}
            activeStep={activeStep}
            setActiveStep={setActiveStep}
            conditionsIsShown={conditionsIsShown}
            setConditionsIsShown={setConditionsIsShown}
            copyRoute={copyRoute}
            onDetailsClick={(event) => {
              const stepNumber = event.data.parentStepNumber || '0';
              if (event.type === StructureOptionType.SHOPPING_CARD) {
                onShoppingCartOpened({ projectId, routeId, stepNumber, routeType, projectInfo });
              } else if (event.type === StructureOptionType.ZOOM) {
                onZoom({ projectId, routeId, stepNumber, routeType, projectInfo });
              } else if (event.type === StructureOptionType.OPTIONS) {
                onHamburgerMenuOpened({ projectId, routeId, stepNumber, routeType, projectInfo });
              }
              structureOptionChecked({
                ...event,
                trackingContext: {
                  projectId,
                  routeId,
                  stepNumber,
                  routeType,
                  projectInfo,
                },
                routeType,
              });
            }}
            onActiveStepChange={onActiveStepChangeHandler}
            onPageLoad={(activeStep) => onPageLoadHandler(projectRouteViewTypes.tree, activeStep)}
            onStepAutoSetWithDelay={onStepAutoSetWithDelayHandler}
            onAddNodeClick={onAddNodeClickHandler}
            openLinkOptions={({ targetRect, step }) => {
              openLinkOptions({ targetRect, step });
              onOptionClick({ projectId, routeId, stepNumber: step.step, routeType, projectInfo });
            }}
            projectInfo={projectInfo}
          />
          <ProjectRouteConditions
            routeType={routeType}
            targetPlacement="docked_bottom"
            activeStep={activeStep}
            setActiveStep={setActiveStep}
            steps={conditionsSteps}
            fetchStepData={fetchStepData}
            placement={conditionsPlacement}
            setPlacement={setConditionsPlacement}
            isShown={conditionsIsShown}
            setIsShown={setConditionsIsShown}
            onUnmount={onConditionsUnmount}
            resized={resized}
            setResized={setResized}
            onResizeAA={onResizeAAHandler}
            onActiveStepChange={onActiveStepChangeHandler}
            onFullTextLinkClick={onConditionsFullTextLinkClickHandler}
            onIssueCitedCounterClick={onConditionsIssueCitedCounterClickHandler}
            onExperimentalProcedureOpen={onConditionsExperimentalProcedureOpenHandler}
            onAbstractOpen={onConditionsAbstractOpenHandler}
            onAuthorClick={onConditionsAuthorClickHandler}
            saveStepSelectionsHandler={saveStepSelectionsHandler}
            loadLastExamples={loadLastExamples}
          />
        </div>
        <ProjectRouteConditions
          routeType={routeType}
          targetPlacement="docked_right"
          activeStep={activeStep}
          setActiveStep={setActiveStep}
          steps={conditionsSteps}
          fetchStepData={fetchStepData}
          placement={conditionsPlacement}
          setPlacement={setConditionsPlacement}
          isShown={conditionsIsShown}
          setIsShown={setConditionsIsShown}
          onUnmount={onConditionsUnmount}
          resized={resized}
          setResized={setResized}
          onActiveStepChange={onActiveStepChangeHandler}
          onFullTextLinkClick={onConditionsFullTextLinkClickHandler}
          onIssueCitedCounterClick={onConditionsIssueCitedCounterClickHandler}
          onExperimentalProcedureOpen={onConditionsExperimentalProcedureOpenHandler}
          onAbstractOpen={onConditionsAbstractOpenHandler}
          onAuthorClick={onConditionsAuthorClickHandler}
          saveStepSelectionsHandler={saveStepSelectionsHandler}
          loadLastExamples={loadLastExamples}
        />
      </div>
      <ProjectRouteConditions
        routeType={routeType}
        targetPlacement="modal"
        activeStep={activeStep}
        setActiveStep={setActiveStep}
        steps={conditionsSteps}
        fetchStepData={fetchStepData}
        placement={conditionsPlacement}
        setPlacement={setConditionsPlacement}
        isShown={conditionsIsShown}
        setIsShown={setConditionsIsShown}
        onUnmount={onConditionsUnmount}
        resized={resized}
        setResized={setResized}
        onActiveStepChange={onActiveStepChangeHandler}
        onFullTextLinkClick={onConditionsFullTextLinkClickHandler}
        onIssueCitedCounterClick={onConditionsIssueCitedCounterClickHandler}
        onExperimentalProcedureOpen={onConditionsExperimentalProcedureOpenHandler}
        onAbstractOpen={onConditionsAbstractOpenHandler}
        onAuthorClick={onConditionsAuthorClickHandler}
        saveStepSelectionsHandler={saveStepSelectionsHandler}
        loadLastExamples={loadLastExamples}
      />
    </div>
  );
}

const mapStateToProps = (state) => ({
  routeType: routeTypeSelector(state),
  treeData: treeDataSelector(state),
  activeStep: activeStepSelector(state),
  conditionsSteps: conditionsStepsSelector(state),
  conditionsPlacement: conditionsPlacementSelector(state),
  conditionsIsShown: conditionsIsShownSelector(state),
  synthesisMenuOptions: getSynthesisMenuOptions(state),
  hasPredictedStep: hasPredictedStep(state),
  exportDialogIsShown: exportDialogIsShown(state),
  addStepsDialogIsShown: addStepsDialogIsShown(state),
  isTransferFromReaction: isTransferFromReaction(state),
  projectsInfoMap: getProjectsInfoMap(state),
  deletedRoute: deletedRoute(state),
});

const mapDispatchToProps = {
  fetchData: fetchDataAction,
  setActiveStep: setActiveStepAction,
  saveStepSelections: saveStepSelectionsAction,
  fetchConditionsStepData: fetchConditionsStepDataAction,
  setConditionsPlacement: setConditionsPlacementAction,
  setConditionsIsShown: setConditionsIsShownAction,
  onConditionsUnmount: onConditionsUnmountAction,
  structureOptionChecked: structureOptionCheckedAction,
  showExportDialog,
  showAddStepsDialog,
  clearStateThumbnails: clearState,
  openLinkOptions: linkOptionsOpenAction,
  copyRoute,
  setIsTransferFromReaction,
  forceReloadThumbnails: forceReload,
  loadLastExamples: fetchPredictedLastStepData,
  clearDeletedRoute,
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(ProjectRoute));
